import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DownloadIcon from '@mui/icons-material/Download';

import AccountDataJsonSections from './AccountDataJsonSections';

const isEmpty = (data) =>
  data && Object.keys(data).length === 0 && data.constructor === Object;

const AccountDataViewer = ({ requestOptions }) => {
  const [accountId, setAccountId] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountData, setAccountData] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  const fetchAccountData = async () => {
    const id = accountId.trim();
    if (!id) return;
    setLoading(true);
    setErrorMessage('');
    setAccountId(id);
    try {
      const response = await fetch(
        `/account-data-distributor/v1/serviceAccounts/${id}`,
        requestOptions
      );
      if (response.ok) {
        setAccountData(await response.json());
      } else {
        setAccountData({});
        setErrorMessage(
          `Error fetching account data: ${response.status} ${response.statusText}`
        );
      }
    } catch (err) {
      setErrorMessage(`Error fetching account data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportJson = () => {
    if (isEmpty(accountData)) return;
    const blob = new Blob([JSON.stringify(accountData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${accountId}_account_data.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h2">Account Data Viewer</Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <TextField
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchAccountData()}
          required
          id="accountId"
          label="Account ID"
          variant="outlined"
          size="small"
          sx={{ minWidth: 220 }}
        />
        <LoadingButton
          variant="contained"
          onClick={fetchAccountData}
          loading={loading}
          disabled={!accountId.trim()}
        >
          Fetch Account Data
        </LoadingButton>
        {!isEmpty(accountData) && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={exportJson}
          >
            Export Raw JSON
          </Button>
        )}
      </Stack>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {!isEmpty(accountData) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <AccountDataJsonSections accountDataJson={accountData} />
          <Box>
            <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1 }}>
              <Typography variant="h3">Raw JSON Data</Typography>
              <Typography variant="body2" color="text.secondary">
                Use ⌘F / Ctrl+F to search
              </Typography>
            </Stack>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                maxHeight: 700,
                overflow: 'auto',
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: '0.8rem',
                lineHeight: 1.5,
              }}
            >
              {JSON.stringify(accountData, null, 2)}
            </Box>
          </Box>
        </Box>
      )}

      {isEmpty(accountData) && !errorMessage && (
        <Typography color="text.secondary">No account data to display.</Typography>
      )}
    </Stack>
  );
};

export default AccountDataViewer;
